import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  AppShell,
  AppShellNavLink,
  IconCalendar,
  IconClub,
  type IconComponent,
  IconGateway,
  IconFile,
  IconGoalPost,
  IconHome,
  IconPlayer,
  IconStaff,
  IconTeam,
  IconTrophy,
  IconUser,
  Skeleton,
  useCollapsedRail,
} from '@volee/ui'
import { lazy, Suspense, type ReactNode } from 'react'

import { meQueryOptions } from '../auth/me.ts'
import { messages } from '../messages/index.ts'
import { isNavItemActive } from './navigation.ts'
import { SignInButton, SignOutButton } from './session-buttons.tsx'

// Deferred: statically imported, the Radix menu cost ~16 kB gzip of first load for a control
// that appears after a click.
const UserMenu = lazy(() => import('./user-menu.tsx').then((m) => ({ default: m.UserMenu })))

// The app shell, from @volee/ui: no frame is built here. The user is a query and not a
// store (§4.4), sharing the guard's cache entry.

/** The footprint of the "Accedi" button, so resolving the session shifts no layout. */
function AuthSlotSkeleton() {
  return <Skeleton className="h-(--size-control-sm) w-24" />
}

interface NavItem {
  readonly to:
    | '/'
    | '/clubs'
    | '/pitches'
    | '/kits'
    | '/players'
    | '/users'
    | '/leagues'
    | '/matches'
    | '/appointments'
    | '/staff'
    | '/bulletin-sources'
  readonly label: string
  /** Required, not decorative: collapsed to its icons, a row without one is a blank square. */
  readonly icon: IconComponent
}

// Every entry requires staff, which is why the shell drops the whole sidebar on a `bare`
// route rather than showing links that all bounce back.
const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: messages.navigation.home, icon: IconHome },
  { to: '/clubs', label: messages.navigation.club, icon: IconClub },
  { to: '/pitches', label: messages.navigation.pitch, icon: IconGoalPost },
  { to: '/kits', label: messages.navigation.kit, icon: IconGateway },
  { to: '/players', label: messages.navigation.player, icon: IconPlayer },
  { to: '/users', label: messages.navigation.users, icon: IconUser },
  { to: '/leagues', label: messages.navigation.leagues, icon: IconTrophy },
  { to: '/matches', label: messages.navigation.matches, icon: IconCalendar },
  { to: '/appointments', label: messages.navigation.register, icon: IconStaff },
  { to: '/staff', label: messages.navigation.staff, icon: IconTeam },
  { to: '/bulletin-sources', label: messages.navigation.bulletinSources, icon: IconFile },
]

export function Layout({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useQuery(meQueryOptions)
  const { collapsed, setCollapsed } = useCollapsedRail()
  // One subscription: the path drives `aria-current`, the href is what signing in returns to.
  const location = useRouterState({ select: (status) => status.location })
  // Declared by the route, not inferred from a list of paths here. See
  // apps/admin-panel/docs/shell-decisa-dalla-route.md.
  const bare =
    useRouterState({ select: (status) => status.matches.at(-1)?.staticData.chrome }) === 'bare'

  // One branch per state of `['me']`, never a boolean: collapsing them is what made "Accedi"
  // flash at a signed-in operator. See apps/admin-panel/docs/utente-corrente-ha-tre-stati.md.
  function headerAuth() {
    if (bare) return null
    if (isPending) return <AuthSlotSkeleton />
    // A refusal is a session that exists, so it gets the way out and never a re-login.
    if (session?.kind === 'refused') return <SignOutButton size="sm" />
    if (session?.kind !== 'user') return <SignInButton returnTo={location.href} size="sm" />
    return (
      <Suspense fallback={<AuthSlotSkeleton />}>
        <UserMenu me={session.me} />
      </Suspense>
    )
  }

  return (
    <AppShell
      navigationLabel={messages.app.navigationLabel}
      openMenuLabel={messages.app.openMenu}
      closeMenuLabel={messages.app.closeMenu}
      collapse={{
        collapsed,
        onCollapsedChange: setCollapsed,
        collapseLabel: messages.app.collapseMenu,
        expandLabel: messages.app.expandMenu,
      }}
      account={headerAuth()}
      centredContent
      sidebar={
        bare
          ? null
          : NAV_ITEMS.map((item) => (
              // `asChild` on the nav link, not on the Link: TanStack's Link does not accept it.
              <AppShellNavLink
                key={item.to}
                asChild
                active={isNavItemActive(item.to, location.pathname)}
                icon={item.icon}
                label={item.label}
              >
                <Link to={item.to} />
              </AppShellNavLink>
            ))
      }
    >
      {children}
    </AppShell>
  )
}
